import { useRef, useState } from 'react';
import { Button } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedProperty, LocalizedPropertyObject, LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { OptimusProvider, QueryBoundary, useConfirmationDialog } from '@mews-commander/ui';
import { AddProductDialogContent } from './AddProductDialogContent';

interface AddProductDialogProps {
    onClose: () => void;
    active: boolean;
    accountId: string;
    serviceId: string;
    billId?: string;
}

export interface AddProductFormRef {
    submit: () => Promise<void>;
    hasDirtyFields: boolean;
}

export const AddProductDialog = ({ active, onClose, ...other }: AddProductDialogProps) => {
    const currentFormRef = useRef<AddProductFormRef | null>(null);
    const [loading, setLoading] = useState(false);
    const [dialogName, setDialogName] = useState<LocalizedPropertyObject | null>(null);

    const { confirmationDialog, openConfirmationDialog } = useConfirmationDialog({
        onConfirm: onClose,
    });

    const handleClose = () => {
        if (currentFormRef.current?.hasDirtyFields) {
            openConfirmationDialog();
        } else {
            onClose();
        }
    };

    const handleSubmit = () => {
        void currentFormRef.current?.submit();
    };

    return (
        <OptimusProvider density="spacious">
            <FloatingPanel
                active={active}
                onClose={handleClose}
                title={
                    <>
                        {lt(TextKey.AddProduct)}
                        {dialogName && (
                            <>
                                {UnicodeEntity.Colon} <LocalizedProperty property={dialogName} />
                            </>
                        )}
                    </>
                }
                footer={{
                    primary: (
                        <Button variant="primary" onClick={handleSubmit} type="submit">
                            <LocalizedText textKey={TextKey.Save} />
                        </Button>
                    ),
                    secondary: (
                        <Button onClick={handleClose} type="submit">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    ),
                }}
                loading={loading}
            >
                <QueryBoundary loader="inline">
                    <AddProductDialogContent
                        currentFormRef={currentFormRef}
                        onClose={onClose}
                        setLoading={setLoading}
                        setDialogName={setDialogName}
                        {...other}
                    />
                    {confirmationDialog}
                </QueryBoundary>
            </FloatingPanel>
        </OptimusProvider>
    );
};
